/**
 * sessionStorage persistence for DB-rendered committed mindmap scopes.
 * Survives deploy-induced render failures and tab refreshes within a session.
 */

import type { CommittedMindmapScopeCacheEntry } from "./pfm-committed-mindmap-cache.ts";

const STORAGE_KEY = "ead-expui.committed-mindmap-scopes.v1";
const MAX_ENTRIES = 48;

type PersistedMap = Record<string, CommittedMindmapScopeCacheEntry>;

function loadAll(): PersistedMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveAll(map: PersistedMap): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

export function getPersistedCommittedMindmapScopeCache(
  key: string,
): CommittedMindmapScopeCacheEntry | null {
  const k = String(key || "").trim();
  if (!k) return null;
  const hit = loadAll()[k];
  if (!hit?.svg) return null;
  return hit;
}

export function setPersistedCommittedMindmapScopeCache(
  key: string,
  entry: CommittedMindmapScopeCacheEntry,
): void {
  const k = String(key || "").trim();
  if (!k || !entry.svg) return;
  const all = loadAll();
  all[k] = entry;
  const keys = Object.keys(all);
  while (keys.length > MAX_ENTRIES) {
    const drop = keys.shift();
    if (drop) delete all[drop];
  }
  saveAll(all);
}

export function clearPersistedCommittedMindmapScopeCacheForExecution(
  executionId: string,
): void {
  const prefix = `${String(executionId || "").trim()}|`;
  if (!prefix || prefix === "|") return;
  const all = loadAll();
  let changed = false;
  for (const key of Object.keys(all)) {
    if (key.startsWith(prefix)) {
      delete all[key];
      changed = true;
    }
  }
  if (changed) saveAll(all);
}
